import type { EntityRegistry } from './entityRegistry';
import type { GameSettings } from './gameSettings';
import type { GameStateSnapshot } from '../network/gameStateSync';
import type { Sheep } from '../weapons/sheep';
import type { SuperSheep } from '../weapons/superSheep';
import type { Kamikaze } from '../weapons/kamikaze';
import type { Fire } from '../entities/fire';
import type { Grave } from '../entities/grave';
import {
  type SerializedGameState,
  type SheepSync,
  type SuperSheepSync,
  type KamikazeSync,
  type FireSync,
  type GraveSync,
  SERIALIZATION_VERSION,
} from '../testing/stateSerializer';

import type { TerrainChange } from '../network/gameStateSync';
import type { LogEntry } from './gameLogger';

export interface SerializationContext {
  entityRegistry: EntityRegistry;
  gameTick: number;
  terrainSeed: number;
  terrainCanvas: HTMLCanvasElement;
  settings: GameSettings;
  currentWaterLevel: number;
  totalTurnCount: number;
  suddenDeathActive: boolean;
  serializeGameState: () => GameStateSnapshot;
  getRngSeed: () => number;
  getTerrainModifications: () => TerrainChange[];
  getLogBuffer: () => LogEntry[];
  getLogCategories: () => Record<string, boolean>;
  gameStartTime: number;
}

export interface ExtendedEntitiesContext {
  entityRegistry: EntityRegistry;
}

export function createSerializedState(ctx: SerializationContext): SerializedGameState {
  const gameState = ctx.serializeGameState();

  const sheep: SheepSync[] = ctx.entityRegistry.getAll<Sheep>('sheep').map((s) => ({
    id: s.id,
    position: { x: s.position.x, y: s.position.y },
    velocity: { x: s.velocity.x, y: s.velocity.y },
    facing: s.facing,
    isGrounded: s.isGrounded,
    active: s.active,
    ownerTeamId: s.ownerTeamId,
    explosionRadius: s.explosionRadius,
    maxDamage: s.maxDamage,
    kind: s.kind,
  }));

  const superSheep: SuperSheepSync[] = ctx.entityRegistry
    .getAll<SuperSheep>('superSheep')
    .map((ss) => ({
      id: ss.id,
      position: { x: ss.position.x, y: ss.position.y },
      velocity: { x: ss.velocity.x, y: ss.velocity.y },
      facing: ss.facing,
      isGrounded: ss.isGrounded,
      active: ss.active,
      ownerTeamId: ss.ownerTeamId,
      isFlying: ss.isFlying,
      flyingAngle: ss.flyingAngle,
      explosionRadius: ss.explosionRadius,
      maxDamage: ss.maxDamage,
    }));

  const kamikazes: KamikazeSync[] = ctx.entityRegistry.getAll<Kamikaze>('kamikaze').map((k) => ({
    id: k.id,
    position: { x: k.position.x, y: k.position.y },
    velocity: { x: k.velocity.x, y: k.velocity.y },
    directionX: k.directionX,
    directionY: k.directionY,
    active: k.active,
    explosionRadius: k.explosionRadius,
    maxDamage: k.maxDamage,
  }));

  const fires: FireSync[] = ctx.entityRegistry.getAll<Fire>('fire').map((f) => ({
    id: f.id,
    position: { x: f.position.x, y: f.position.y },
    velocity: { x: f.velocity.x, y: f.velocity.y },
    active: f.active,
    remainingDuration: f.getRemainingLife() * f.duration,
  }));

  const graves: GraveSync[] = ctx.entityRegistry.getAll<Grave>('grave').map((g) => ({
    id: g.id,
    position: { x: g.position.x, y: g.position.y },
    velocity: { x: g.velocity.x, y: g.velocity.y },
    active: g.active,
    health: g.health,
  }));

  return {
    version: SERIALIZATION_VERSION,
    timestamp: Date.now(),
    tick: ctx.gameTick,
    terrainSeed: ctx.terrainSeed,
    rngState: { seed: ctx.getRngSeed() },
    gameState,
    terrainData: '',
    terrainModifications: ctx.getTerrainModifications(),
    terrainDimensions: {
      width: ctx.terrainCanvas.width,
      height: ctx.terrainCanvas.height,
    },
    settings: ctx.settings,
    extendedEntities: { sheep, superSheep, kamikazes, fires, graves },
    waterLevel: ctx.currentWaterLevel,
    totalTurnCount: ctx.totalTurnCount,
    suddenDeathActive: ctx.suddenDeathActive,
    logs: {
      entries: ctx.getLogBuffer(),
      sessionStart: ctx.gameStartTime,
      enabledCategories: ctx.getLogCategories(),
    },
  };
}

export function restoreExtendedEntities(
  extended: SerializedGameState['extendedEntities'],
  ctx: ExtendedEntitiesContext,
): void {
  for (const sheepData of extended.sheep) {
    const sheep = ctx.entityRegistry.get<Sheep>('sheep', sheepData.id);
    if (sheep) {
      sheep.position.x = sheepData.position.x;
      sheep.position.y = sheepData.position.y;
      sheep.velocity.x = sheepData.velocity.x;
      sheep.velocity.y = sheepData.velocity.y;
      sheep.facing = sheepData.facing;
      sheep.isGrounded = sheepData.isGrounded;
      sheep.active = sheepData.active;
    }
  }

  for (const ssData of extended.superSheep) {
    const ss = ctx.entityRegistry.get<SuperSheep>('superSheep', ssData.id);
    if (ss) {
      ss.position.x = ssData.position.x;
      ss.position.y = ssData.position.y;
      ss.velocity.x = ssData.velocity.x;
      ss.velocity.y = ssData.velocity.y;
      ss.facing = ssData.facing;
      ss.isGrounded = ssData.isGrounded;
      ss.active = ssData.active;
      ss.isFlying = ssData.isFlying;
      ss.flyingAngle = ssData.flyingAngle;
    }
  }

  for (const graveData of extended.graves) {
    const grave = ctx.entityRegistry.get<Grave>('grave', graveData.id);
    if (grave) {
      grave.position.x = graveData.position.x;
      grave.position.y = graveData.position.y;
      grave.velocity.x = graveData.velocity.x;
      grave.velocity.y = graveData.velocity.y;
      grave.active = graveData.active;
      grave.health = graveData.health;
    }
  }
}
